/**
 * UnansweredLeadsBanner — proaktywny baner na górze panelu.
 * „Masz X nieodebranych zapytań — odpowiedz teraz →". Można zamknąć na dziś.
 * Ukrywa się gdy count===0, na stronie zapytań i w kreatorach.
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Inbox, ArrowRight, X } from 'lucide-react';

const leadWord = (n) => {
  if (n === 1) return 'nieodebrane zapytanie';
  const d = n % 10, h = n % 100;
  if (d >= 2 && d <= 4 && !(h >= 12 && h <= 14)) return 'nieodebrane zapytania';
  return 'nieodebranych zapytań';
};

const dismissKey = () => `apisai_leads_banner_dismissed_${new Date().toISOString().slice(0, 10)}`;

export default function UnansweredLeadsBanner({ count = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey()) === '1'; } catch { return false; }
  });

  if (!count || count <= 0) return null;
  if (dismissed) return null;
  if (location.pathname.startsWith('/panel/leady')) return null;
  if (location.pathname.startsWith('/wizard')) return null;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey(), '1'); } catch {}
    setDismissed(true);
  };

  return (
    <div
      data-testid="unanswered-leads-banner"
      className="mx-3 sm:mx-6 mt-4 rounded-2xl border border-[var(--apisai-primary)]/25 bg-gradient-to-r from-[var(--apisai-primary)]/[0.09] to-transparent p-3.5 sm:p-4 flex items-center gap-3"
    >
      <div className="w-10 h-10 rounded-xl bg-[var(--apisai-primary)]/15 flex items-center justify-center flex-shrink-0">
        <Inbox className="w-5 h-5 text-[var(--apisai-primary)]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-bold text-[var(--apisai-text-primary)] leading-snug">
          Masz {count} {leadWord(count)}
        </p>
        <p className="text-[12px] text-[var(--apisai-text-secondary)]">
          Odpowiedz teraz — szybka reakcja zwiększa szansę na klienta.
        </p>
      </div>
      <button
        onClick={() => navigate('/panel/leady')}
        data-testid="unanswered-leads-banner-cta"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--apisai-primary)] text-white text-[13px] font-semibold hover:brightness-95 transition-all flex-shrink-0"
      >
        Odpowiedz teraz <ArrowRight className="w-4 h-4" />
      </button>
      <button
        onClick={dismiss}
        data-testid="unanswered-leads-banner-dismiss"
        aria-label="Zamknij na dziś"
        className="w-8 h-8 rounded-lg flex items-center justify-center text-[var(--apisai-text-muted)] hover:bg-black/5 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
